import { type CSSProperties, type ReactNode, type FC } from 'react'
import classnames from 'classnames'
import { type IIconsVector, Icon } from 'shared/ui/Icon'
import { Typography } from 'shared/ui/Typography'
import { Tooltip, type ITooltipProps } from 'shared/ui/Tooltip'
import { getAriaLabel } from 'shared/lib'
import styles from './styles.module.scss'

export type ILabel = {
  text?: ReactNode
  textColor?: CSSProperties['color']
  secondaryText?: ReactNode
  rightText?: ReactNode
  className?: string
  textClassName?: string
  width?: number
  leftIcon?: IIconsVector
  rightIcon?: IIconsVector
  horizontal?: boolean
  rightNode?: ReactNode
}

export type ILabelProps = {
  label?: ILabel
  tertiary?: boolean
  height?: CSSProperties['height']
  boxAlignItems?: CSSProperties['alignItems']
  questionTooltipProps?: ITooltipProps
  marginBottom?: CSSProperties['marginBottom']
  marginTop?: CSSProperties['marginTop']
}

export const Label: FC<ILabelProps> = ({
  label,
  questionTooltipProps,
  boxAlignItems,
  height,
  tertiary = true,
  marginBottom,
  marginTop,
}) => {
  if (!label) return null

  const {
    text,
    secondaryText,
    rightText,
    width,
    leftIcon,
    rightIcon,
    horizontal,
    textColor,
    textClassName,
    rightNode,
  } = label

  const rightContent = () => {
    if (rightNode) {
      return rightNode
    }

    return (
      <Typography variant={'text-input-field'} className={'tertiary'} ariaLabel={'rightText'}>
        {rightText}
      </Typography>
    )
  }

  return (
    <div
      className={classnames(styles.wrapLabel, label.className)}
      style={{ minWidth: width, height, marginBottom, marginTop }}
      aria-label={getAriaLabel('Label')}
    >
      <div
        className={styles.boxLabel}
        style={{ alignItems: boxAlignItems }}
        aria-label={getAriaLabel('Label', 'boxLabel')}
      >
        {leftIcon && <Icon icon={leftIcon} fontSize={16} tertiary={tertiary} />}
        <Typography
          variant={horizontal ? 'text-input-field' : 'text-input-label-small'}
          className={classnames(horizontal ? '' : tertiary && 'tertiary', textClassName)}
          ariaLabel={'text'}
          color={textColor}
        >
          {text}
        </Typography>
        {secondaryText && (
          <Typography
            variant={'text-input-label-small'}
            color={'var(--content-neutral-primary)'}
            ariaLabel={'secondaryText'}
          >
            {secondaryText}
          </Typography>
        )}
        {rightIcon && <Icon icon={rightIcon} fontSize={16} tertiary={tertiary} />}
        {questionTooltipProps && (
          <Tooltip placement={'top'} {...questionTooltipProps}>
            <Icon
              className={styles.questionIcon}
              icon={'question'}
              color={'var(--content-neutral-primary)'}
              fontSize={12}
            />
          </Tooltip>
        )}
      </div>
      <div className={styles.boxLabel} aria-label={getAriaLabel('Label', 'boxLabel')}>
        {(rightText || rightNode) && rightContent()}
      </div>
    </div>
  )
}
